'use client';

/*
 * TEMPORARY FORK of @kraidle/ui v0.21.0 `blocks/capabilities-page.tsx`, until
 * kraidle exposes an after-hero slot (follow-up: add `afterHero` to kraidle's
 * CapabilitiesPage, then delete this file and render kraidle's block directly).
 *
 * Relative imports are rewritten to package paths and the file is
 * Prettier-formatted (repo quotes + Tailwind class order; no class added or
 * removed). The ONLY behavioural change is the `afterHero` slot (marked `SLOT`
 * below): full-width content between the hero and the Feature overview (the
 * key-metrics band). With `afterHero` unset it renders exactly what kraidle's
 * CapabilitiesPage renders. When bumping @kraidle/ui, re-diff against
 * node_modules/@kraidle/ui/src/blocks/capabilities-page.tsx (prettier both first);
 * capabilities-page-with-after-hero.test.ts fails when that file changes.
 * Only the product route uses this, and only for slugs with key metrics.
 */

import type { ReactNode } from 'react';

import { Hero } from '@kraidle/ui/components/hero';
import {
  FeatureOverview,
  FeatureOverviewContent,
  FeatureOverviewDescription,
  FeatureOverviewDivider,
  FeatureOverviewGroup,
  FeatureOverviewGroups,
  FeatureOverviewTitle,
} from '@kraidle/ui/components/feature-overview';
import {
  HorizontalMediaCard,
  HorizontalMediaCardContent,
  HorizontalMediaCardDivider,
  HorizontalMediaCardMedia,
  HorizontalMediaCardTitle,
} from '@kraidle/ui/components/horizontal-media-card';
import { ResourcesSection } from '@kraidle/ui/components/resources-section';
import { CTASection } from '@kraidle/ui/components/cta-section';
import { Footer } from '@kraidle/ui/components/footer';
import { Badge } from '@kraidle/ui/components/badge';
import { Link } from '@kraidle/ui/components/link';

import { isExternalHref } from '@kraidle/ui/lib/pages/resolve';
import {
  KraidleAdapterProvider,
  type LinkComponent,
  type ImageComponent,
  type ResolveHref,
} from '@kraidle/ui/lib/adapter';
import type { Bullet, Lens, ResolvedPage } from '@kraidle/ui/lib/pages/types';

import { InlineMarkdown } from '@kraidle/ui/blocks/markdown';
import { AssetVisual } from '@kraidle/ui/blocks/asset-visual';
import { CapabilityTabs } from '@kraidle/ui/blocks/capability-tabs';
import { RESOURCE_KIND_LABEL, resourceIcon } from '@kraidle/ui/blocks/resource-icons';

const LENS_LABEL: Record<Lens, string> = {
  observability: 'Observability',
  analytics: 'Analytics',
  governance: 'Governance',
  'agent-context': 'Agent Context',
};

function normalizeBullet(b: Bullet): { text: string; roadmap: boolean } {
  return typeof b === 'string' ? { text: b, roadmap: false } : { text: b.text, roadmap: Boolean(b.roadmap) };
}

function humanizeSlug(slug: string): string {
  return slug
    .replace(/\/$/, '')
    .split('/')
    .pop()!
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * kraidle's CapabilitiesPage plus an `afterHero` slot (see the header comment).
 *
 * @param chrome When true (default) the block renders its own `<main>` wrapper
 * and a trailing `<Footer/>` — the standalone-page shape. Set `chrome={false}`
 * to render just the page body in a `<div>` (no footer) so it composes inside a
 * consumer's existing app layout (nav + main + footer) without a duplicate
 * `<main>` or footer.
 *
 * @param linkComponent Optional link component (e.g. `next/link`) injected into
 * every internal link on the page via the adapter seam. Defaults to a plain
 * `<a>`.
 * @param imageComponent Optional image component (e.g. a `next/image` wrapper)
 * for page assets. Defaults to a plain `<img>`.
 * @param resolveHref Optional resolver for bare internal slugs in markdown.
 */
export function CapabilitiesPageWithAfterHero({
  page,
  chrome = true,
  linkComponent,
  imageComponent,
  resolveHref,
  afterHero,
}: {
  page: ResolvedPage;
  chrome?: boolean;
  linkComponent?: LinkComponent;
  imageComponent?: ImageComponent;
  resolveHref?: ResolveHref;
  /** SLOT: full-width content between the hero and the Feature overview. */
  afterHero?: ReactNode;
}) {
  const { meta, hero, overview, sidebar, deep_dives, resources, whats_next } = page;
  const { see_also, works_great_with } = sidebar;
  const Wrapper = chrome ? 'main' : 'div';

  return (
    <KraidleAdapterProvider linkComponent={linkComponent} imageComponent={imageComponent} resolveHref={resolveHref}>
      <Wrapper className="flex flex-col">
        <Hero
          title={hero.title}
          description={hero.subhead}
          primaryLabel={hero.cta[0]?.label}
          primaryHref={hero.cta[0]?.href}
          secondaryLabel={hero.cta[1]?.label}
          secondaryHref={hero.cta[1]?.href}
          media={hero.visual ? <AssetVisual asset={hero.visual} /> : undefined}
        />

        {/* SLOT */}
        {afterHero}

        {/* Subtle background band sets the overview apart from the detail
          sections below — the page reads as "overview → deep dives". */}
        <FeatureOverview outerClassName="bg-background-subtle">
          <FeatureOverviewContent>
            <p className="text-text-secondary text-eyebrow">Feature overview</p>
            <FeatureOverviewTitle>{meta.title}</FeatureOverviewTitle>
            <FeatureOverviewDescription>
              <InlineMarkdown>{overview}</InlineMarkdown>
            </FeatureOverviewDescription>
          </FeatureOverviewContent>
          {see_also.length > 0 || works_great_with.length > 0 ? (
            <>
              <FeatureOverviewDivider />
              <FeatureOverviewGroups>
                {see_also.length > 0 ? (
                  <FeatureOverviewGroup eyebrow="See also">
                    {see_also.map(c => (
                      <Link key={c.href} href={c.href} external={isExternalHref(c.href)}>
                        {c.label}
                      </Link>
                    ))}
                  </FeatureOverviewGroup>
                ) : null}
                {works_great_with.length > 0 ? (
                  <FeatureOverviewGroup eyebrow="Works great with">
                    {works_great_with.map(w => (
                      <Link key={w.href} href={w.href}>
                        {humanizeSlug(w.capability)}
                      </Link>
                    ))}
                  </FeatureOverviewGroup>
                ) : null}
              </FeatureOverviewGroups>
            </>
          ) : null}
        </FeatureOverview>

        {deep_dives.length > 0 ? (
          <>
            <CapabilityTabs
              items={deep_dives.map(d => ({
                href: `#${d.id}`,
                label: d.tab,
              }))}
            />

            {deep_dives.map((d, i) => (
              <HorizontalMediaCard
                key={d.id}
                id={d.id}
                mediaPosition={i % 2 === 0 ? 'left' : 'right'}
                // Clear both sticky bars (nav + tabs) when a tab click scrolls here.
                // Fallbacks (104px) degrade gracefully if a consumer omits the vars.
                className="scroll-mt-[calc(var(--nav-height,104px)+var(--tabs-height,104px))]"
              >
                <HorizontalMediaCardContent>
                  {d.lens ? <Badge variant="outline">{LENS_LABEL[d.lens]}</Badge> : null}
                  <HorizontalMediaCardTitle as="h2">{d.headline}</HorizontalMediaCardTitle>
                  <ul className="space-y-md pl-lg marker:text-text-tertiary list-disc">
                    {d.bullets.map((raw, bi) => {
                      const { text, roadmap } = normalizeBullet(raw);
                      return (
                        <li key={bi} className="pl-xs text-text-secondary text-body-lg">
                          {roadmap ? (
                            <Badge variant="roadmap" className="mr-sm align-middle">
                              Roadmap
                            </Badge>
                          ) : null}
                          <span>
                            <InlineMarkdown>{text}</InlineMarkdown>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {/* Inline named-attribution evidence riding this section's claim. */}
                  {d.evidence && d.evidence.length > 0 ? (
                    <div className="gap-sm border-border-default pl-md flex flex-col border-l-2">
                      {d.evidence.map((ev, ei) => (
                        <p key={ei} className="text-text-secondary text-body-md">
                          <span className="text-text-primary font-medium">{ev.customer}: </span>
                          <InlineMarkdown>{ev.metric}</InlineMarkdown>
                        </p>
                      ))}
                    </div>
                  ) : null}
                  {d.doc ? (
                    <Link href={d.doc.href} external={isExternalHref(d.doc.href)}>
                      {d.doc.label ?? 'View documentation'}
                    </Link>
                  ) : null}
                </HorizontalMediaCardContent>
                <HorizontalMediaCardDivider />
                <HorizontalMediaCardMedia>
                  {d.visual ? <AssetVisual asset={d.visual} /> : null}
                </HorizontalMediaCardMedia>
              </HorizontalMediaCard>
            ))}
          </>
        ) : null}

        {resources.length > 0 ? (
          <ResourcesSection
            title="Resources"
            titleId="capability-resources-title"
            resources={resources.map(r => ({
              title: r.title,
              description: RESOURCE_KIND_LABEL[r.kind],
              icon: resourceIcon(r.kind),
              linkLabel: 'Learn more',
              linkHref: r.href ?? '#',
              linkExternal: r.href ? isExternalHref(r.href) : false,
            }))}
          />
        ) : null}

        <CTASection
          title="What's next"
          titleId="capability-cta-section-title"
          description={whats_next.lead}
          primaryLabel={whats_next.button.label}
          primaryHref={whats_next.button.href}
          links={whats_next.learn_more.map(c => ({
            label: c.label,
            href: c.href,
            external: isExternalHref(c.href),
          }))}
        />

        {chrome ? <Footer /> : null}
      </Wrapper>
    </KraidleAdapterProvider>
  );
}
